import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from './auth.service';

type AccessEvaluator = (authService: AuthService) => boolean;

function requireAccess(evaluator: AccessEvaluator): CanActivateFn {
  return () => {
    const authService = inject(AuthService);
    const router = inject(Router);

    return evaluator(authService)
      ? true
      : router.createUrlTree(['/login']);
  };
}

export const requireAuth: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.authStatus !== 'authenticated') {
    return router.createUrlTree(['/login']);
  }

  if (authService.tokenInvalidOrExpired(authService.token)) {
    authService.resetSession();

    return router.createUrlTree(['/login']);
  }

  return true;
};

export const requireAdminAccess = requireAccess(
  (authService) => authService.canManageUsersSync,
);

export const requireAdminProjectsAccess = requireAccess(
  (authService) => authService.hasAdminProjectsAccessSync,
);

export const requireAdminAreaAccess = requireAccess(
  (authService) => authService.hasAdminProjectsAccessRuntimeSync || authService.canAccessProductionViewRuntimeSync,
);

export const requireCanAdvanceOrderStatus = requireAccess(
  (authService) => authService.canAdvanceOrderStatusRuntimeSync,
);

export const requireProductionViewAccess = requireAccess(
  (authService) => authService.canAccessProductionViewRuntimeSync,
);

export const requireAdminOrgEventsAccess = requireAccess(
  (authService) => authService.canViewAdminOrgEventsSync,
);

export const requireCustomerProfilesAdminAccess = requireAccess(
  (authService) => authService.hasCustomerProfilesAdminAccessSync,
);

export const requireBatteryCatalogAdminAccess = requireAccess(
  (authService) => authService.canAccessBatteryCatalogAdminRuntimeSync,
);

export const requireFullAdminOrgAccess = requireAccess(
  (authService) => authService.hasFullAdminOrgAccessSync,
);

export const requireResellerEventsAccess = requireAccess(
  (authService) => authService.canViewResellerEventsSync,
);

export const requireResellerWorkspaceAccess = requireAccess(
  (authService) => authService.canAccessResellerWorkspaceSync,
);

export const requireResellerAdminAccess = requireAccess(
  (authService) => authService.isResellerAdminRuntimeSync,
);

export const requireQuoteTemplateAccess = requireAccess(
  (authService) => authService.canManageQuoteTemplatesRuntimeSync,
);

export const requireSuperadminAccess = requireAccess(
  (authService) => authService.isSuperAdminSync,
);
